import {ChangeEvent, FC} from 'react';
import {FormField} from 'ui';
import {
	DocumentPhotoSlotValue,
	DocumentsSlotConfig,
	extractDateDigits,
	extractDepartmentCodeDigits,
	extractPassportSeriesNumberDigits,
	formatBelarusPassportNumberDisplay,
	formatDateDisplay,
	formatDepartmentCodeDisplay,
	formatPassportSeriesNumberDisplay,
	getBirthDateError,
	getIssuedAtDateError,
	normalizeBelarusPassportNumber,
	normalizePassportIdentificationNumber,
	PassportDetailsStepValues,
} from '../../model';
import {PassportPhotoMultiUpload} from '../components/PassportPhotoUpload/PassportPhotoMultiUpload';
import {StepLayout} from 'features/registration';
import {TextInput} from '../components/TextInput';
import styles from './PassportDetailsStep.module.css';

type Props = {
	values: PassportDetailsStepValues;
	onChange: (patch: Partial<PassportDetailsStepValues>) => void;
	title?: string;
	showRegistrationAddress?: boolean;
	showIdentificationNumber?: boolean;
	showLatinNames?: boolean;
	showDepartmentCode?: boolean;
	/** Беларусь: номер паспорта `AA0000000` вместо серии/номера РФ. */
	belarusPassportNumber?: boolean;
	/** Слоты фото на этом же экране (РФ/Беларусь паспорт). */
	photoSlots?: DocumentsSlotConfig[];
	photoGroupLabel?: string;
	photoValues?: Record<string, DocumentPhotoSlotValue>;
	onPhotoSelect?: (slotId: string, file: File) => void;
	onPhotoRemove?: (slotId: string) => void;
};

export const PassportDetailsStep: FC<Props> = ({
	values,
	onChange,
	title = 'Введите паспортные данные',
	showRegistrationAddress = true,
	showIdentificationNumber = false,
	showLatinNames = false,
	showDepartmentCode = true,
	belarusPassportNumber = false,
	photoSlots,
	photoGroupLabel = 'Фото паспорта',
	photoValues,
	onPhotoSelect,
	onPhotoRemove,
}) => {
	const handleSeriesNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
		onChange({
			seriesNumber: belarusPassportNumber
				? normalizeBelarusPassportNumber(event.target.value)
				: extractPassportSeriesNumberDigits(event.target.value),
		});
	};

	const handleDepartmentCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
		onChange({departmentCode: extractDepartmentCodeDigits(event.target.value)});
	};

	const handleIssuedAtChange = (event: ChangeEvent<HTMLInputElement>) => {
		onChange({issuedAt: extractDateDigits(event.target.value)});
	};

	const handleBirthDateChange = (event: ChangeEvent<HTMLInputElement>) => {
		onChange({birthDate: extractDateDigits(event.target.value)});
	};

	const issuedAtError = getIssuedAtDateError(values.issuedAt);
	const birthDateError = getBirthDateError(values.birthDate);
	const showPhotos = Boolean(photoSlots?.length && onPhotoSelect && onPhotoRemove);

	return (
		<StepLayout>
			<StepLayout.Title>{title}</StepLayout.Title>

			<StepLayout.Content className={styles.fields}>
				{showPhotos ? (
					/* Не FormField (<label>): внутри слоты со своими label — вложенные label ломают file input. */
					<div className={styles.photoBlock}>
						<span className={styles.photoGroupLabel}>{photoGroupLabel}</span>
						<PassportPhotoMultiUpload
							items={photoSlots!.map((slot) => ({
								id: slot.id,
								label: slot.label,
								previewUrl: photoValues?.[slot.id]?.previewUrl ?? null,
							}))}
							onSelect={onPhotoSelect!}
							onRemove={onPhotoRemove!}
						/>
					</div>
				) : null}

				<FormField label={belarusPassportNumber ? 'Номер паспорта' : 'Серия и номер'}>
					<TextInput
						name="passportSeriesNumber"
						value={
							belarusPassportNumber
								? formatBelarusPassportNumberDisplay(values.seriesNumber)
								: formatPassportSeriesNumberDisplay(values.seriesNumber)
						}
						placeholder={belarusPassportNumber ? 'AA0000000' : '0000 000000'}
						inputMode={belarusPassportNumber ? 'text' : 'numeric'}
						autoComplete="off"
						onChange={handleSeriesNumberChange}
						onClear={() => onChange({seriesNumber: ''})}
					/>
				</FormField>

				{showIdentificationNumber ? (
					<FormField label="Идентификационный номер">
						<TextInput
							name="passportIdentificationNumber"
							value={values.identificationNumber}
							placeholder="0000000A000AA0"
							autoComplete="off"
							onChange={(event) =>
								onChange({
									identificationNumber: normalizePassportIdentificationNumber(event.target.value),
								})
							}
							onClear={() => onChange({identificationNumber: ''})}
						/>
					</FormField>
				) : null}

				{showLatinNames ? (
					<>
						<FormField label="Фамилия (на латинице)">
							<TextInput
								name="passportSecondNameLatin"
								value={values.secondNameLatin}
								placeholder="Ivanov"
								autoComplete="off"
								onChange={(event) => onChange({secondNameLatin: event.target.value})}
								onClear={() => onChange({secondNameLatin: ''})}
							/>
						</FormField>

						<FormField label="Имя (на латинице)">
							<TextInput
								name="passportLatinName"
								value={values.latinName}
								placeholder="Ivan"
								autoComplete="off"
								onChange={(event) => onChange({latinName: event.target.value})}
								onClear={() => onChange({latinName: ''})}
							/>
						</FormField>
					</>
				) : null}

				<FormField label="Кем выдан">
					<TextInput
						name="passportIssuer"
						multiline
						value={values.issuer}
						placeholder="Орган выдачи"
						autoComplete="off"
						onChange={(event) => onChange({issuer: event.target.value})}
						onClear={() => onChange({issuer: ''})}
					/>
				</FormField>

				<FormField label="Дата выдачи" error={issuedAtError}>
					<TextInput
						name="passportIssuedAt"
						value={formatDateDisplay(values.issuedAt)}
						placeholder="01.01.2000"
						inputMode="numeric"
						autoComplete="off"
						aria-invalid={Boolean(issuedAtError)}
						onChange={handleIssuedAtChange}
						onClear={() => onChange({issuedAt: ''})}
					/>
				</FormField>

				{showDepartmentCode ? (
					<FormField label="Код подразделения">
						<TextInput
							name="passportDepartmentCode"
							value={formatDepartmentCodeDisplay(values.departmentCode)}
							placeholder="000-000"
							inputMode="numeric"
							autoComplete="off"
							onChange={handleDepartmentCodeChange}
							onClear={() => onChange({departmentCode: ''})}
						/>
					</FormField>
				) : null}

				<FormField label="Дата рождения" error={birthDateError}>
					<TextInput
						name="passportBirthDate"
						value={formatDateDisplay(values.birthDate)}
						placeholder="01.01.2000"
						inputMode="numeric"
						autoComplete="bday"
						aria-invalid={Boolean(birthDateError)}
						onChange={handleBirthDateChange}
						onClear={() => onChange({birthDate: ''})}
					/>
				</FormField>

				<FormField label="Место рождения">
					<TextInput
						name="passportBirthPlace"
						multiline
						value={values.birthPlace}
						placeholder="г. Москва"
						autoComplete="off"
						onChange={(event) => onChange({birthPlace: event.target.value})}
						onClear={() => onChange({birthPlace: ''})}
					/>
				</FormField>

				{showRegistrationAddress ? (
					<FormField label="Адрес регистрации">
						<TextInput
							name="passportRegistrationAddress"
							multiline
							value={values.registrationAddress}
							placeholder="До номера квартиры"
							autoComplete="off"
							onChange={(event) => onChange({registrationAddress: event.target.value})}
							onClear={() => onChange({registrationAddress: ''})}
						/>
					</FormField>
				) : null}
			</StepLayout.Content>
		</StepLayout>
	);
};
